/**
 * Mapa de valores semânticos para seus equivalentes CSS.
 */
type SemanticMap = Record<string, string>

/**
 * Função de validação para valores dinâmicos de token.
 *
 * @param value Valor a ser validado.
 * @returns Indica se o valor é considerado válido.
 */
type Validator = (value: string) => boolean

/**
 * Resolve um valor de token aplicando validação e fallback.
 *
 * @param raw Valor bruto informado.
 * @param options Configurações de resolução.
 * @param options.fallback Valor padrão utilizado quando `raw` é inválido.
 * @param options.semanticMap Mapa opcional de valores semânticos.
 * @param options.validators Lista opcional de validadores.
 *
 * @returns Valor final resolvido.
 *
 * @remarks
 * Ordem de resolução:
 * 1. Retorna fallback se `raw` for vazio ou indefinido.
 * 2. Retorna o valor do mapa semântico, se aplicável.
 * 3. Retorna `raw` se passar por algum validador.
 * 4. Caso contrário, retorna o fallback.
 */
export function resolveTokenValue(
  raw: string | undefined,
  options: {
    fallback: string
    semanticMap?: SemanticMap
    validators?: Validator[]
  }
): string {
  if (raw === undefined || raw === '') return options.fallback

  const { semanticMap, validators = [], fallback } = options

  if (semanticMap && raw in semanticMap) {
    return semanticMap[raw]
  }

  const isValid = validators.some((validator) => validator(raw))

  return isValid ? raw : fallback
}

/**
 * Valida valores positivos em pixels.
 *
 * @example 16px
 */
export const positivePxValidator: Validator = (value) => /^\d+(\.\d+)?px$/.test(value) && parseFloat(value) > 0

/**
 * Valida valores positivos em frações de grid.
 *
 * @example 1fr
 */
export const positiveFrValidator: Validator = (value) => /^\d+(\.\d+)?fr$/.test(value) && parseFloat(value) > 0
